import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { 
  Users, 
  Calendar,
  Clock,
  UserPlus,
  Building2,
  Briefcase,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  UserCheck,
  UserMinus,
  ClipboardCheck,
  AlertCircle,
  CheckCircle2,
  XCircle,
  LogIn,
  LogOut,
  Coffee,
  Timer,
  Palmtree
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

export default function WorkforcePage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  
  // Week navigation for scheduler
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Dialog states
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    department_id: '',
    position_id: '',
    hire_date: format(new Date(), 'yyyy-MM-dd'),
    employment_type: 'FULL_TIME'
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    employee_id: '',
    schedule_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    department_id: ''
  });
  
  const [leaveForm, setLeaveForm] = useState({
    employee_id: '',
    leave_type: 'ANNUAL',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    reason: ''
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');

  useEffect(() => {
    if (currentProperty?.id) {
      loadDashboard();
      loadDepartments();
      loadPositions();
      loadShifts();
    }
  }, [currentProperty]);

  useEffect(() => {
    if (currentProperty?.id && activeTab === 'employees') {
      loadEmployees();
    } else if (currentProperty?.id && activeTab === 'scheduler') {
      loadEmployees();
      loadSchedules();
    } else if (currentProperty?.id && activeTab === 'timesheet') {
      loadTimeEntries();
    } else if (currentProperty?.id && activeTab === 'leave') {
      loadLeaveRequests();
    }
  }, [currentProperty, activeTab, currentWeekStart]);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/workforce/dashboard?property_id=${currentProperty.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      let url = `/workforce/employees?property_id=${currentProperty.id}`;
      if (searchQuery) url += `&search=${searchQuery}`;
      if (filterDepartment && filterDepartment !== 'all') url += `&department_id=${filterDepartment}`;
      
      const response = await api.get(url);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await api.get(`/workforce/departments?property_id=${currentProperty.id}`);
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadPositions = async () => {
    try {
      const response = await api.get(`/workforce/positions?property_id=${currentProperty.id}`);
      setPositions(response.data || []);
    } catch (error) {
      console.error('Error loading positions:', error);
    }
  };

  const loadShifts = async () => {
    try {
      const response = await api.get(`/workforce/shifts?property_id=${currentProperty.id}`);
      setShifts(response.data || []);
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const response = await api.get(
        `/workforce/schedules?property_id=${currentProperty.id}&start_date=${format(currentWeekStart, 'yyyy-MM-dd')}&end_date=${format(weekEnd, 'yyyy-MM-dd')}`
      );
      setSchedules(response.data || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const loadTimeEntries = async () => {
    try {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const response = await api.get(
        `/workforce/time-entries?property_id=${currentProperty.id}&start_date=${format(currentWeekStart, 'yyyy-MM-dd')}&end_date=${format(weekEnd, 'yyyy-MM-dd')}`
      );
      setTimeEntries(response.data || []);
    } catch (error) {
      console.error('Error loading time entries:', error);
    }
  };

  const loadLeaveRequests = async () => {
    try {
      const response = await api.get(`/workforce/leave-requests?property_id=${currentProperty.id}`);
      setLeaveRequests(response.data || []);
    } catch (error) {
      console.error('Error loading leave requests:', error);
    }
  };

  const handleCreateEmployee = async () => {
    try {
      const payload = {
        ...employeeForm,
        department_id: employeeForm.department_id || null,
        position_id: employeeForm.position_id || null,
        email: employeeForm.email || null,
        phone: employeeForm.phone || null,
      };
      await api.post(`/workforce/employees?property_id=${currentProperty.id}`, payload);
      toast.success('Employee created successfully');
      setShowEmployeeDialog(false);
      loadEmployees();
      loadDashboard();
      resetEmployeeForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  const handleCreateSchedule = async () => {
    try {
      await api.post(`/workforce/schedules?property_id=${currentProperty.id}`, scheduleForm);
      toast.success('Schedule created successfully');
      setShowScheduleDialog(false);
      loadSchedules();
      resetScheduleForm();
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const handleCreateLeaveRequest = async () => {
    try {
      await api.post(`/workforce/leave-requests?property_id=${currentProperty.id}`, leaveForm);
      toast.success('Leave request submitted');
      setShowLeaveDialog(false);
      loadLeaveRequests();
      resetLeaveForm();
    } catch (error) {
      toast.error('Failed to submit leave request');
    }
  };

  const handleApproveLeave = async (requestId) => {
    try {
      await api.post(`/workforce/leave-requests/${requestId}/approve`);
      toast.success('Leave request approved');
      loadLeaveRequests();
    } catch (error) {
      toast.error('Failed to approve leave request');
    }
  };

  const handleRejectLeave = async (requestId) => {
    try {
      await api.post(`/workforce/leave-requests/${requestId}/reject?reason=Not approved`);
      toast.success('Leave request rejected');
      loadLeaveRequests();
    } catch (error) {
      toast.error('Failed to reject leave request');
    }
  };

  const handleApproveTimeEntry = async (entryId) => {
    try {
      await api.post(`/workforce/time-entries/${entryId}/approve`);
      toast.success('Time entry approved');
      loadTimeEntries();
    } catch (error) {
      toast.error('Failed to approve time entry');
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      employee_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      department_id: '',
      position_id: '',
      hire_date: format(new Date(), 'yyyy-MM-dd'),
      employment_type: 'FULL_TIME'
    });
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      employee_id: '',
      schedule_date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '17:00',
      department_id: ''
    });
  };

  const resetLeaveForm = () => {
    setLeaveForm({
      employee_id: '',
      leave_type: 'ANNUAL',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      reason: ''
    });
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(currentWeekStart, i));
    }
    return days;
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE': return 'default';
      case 'ON_LEAVE': return 'secondary';
      case 'SUSPENDED': return 'destructive';
      case 'PENDING': return 'outline';
      case 'APPROVED': return 'default';
      case 'REJECTED': return 'destructive';
      default: return 'secondary';
    }
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      ANNUAL: 'Annual Leave',
      SICK: 'Sick Leave',
      PERSONAL: 'Personal',
      MATERNITY: 'Maternity',
      PATERNITY: 'Paternity',
      UNPAID: 'Unpaid',
      BEREAVEMENT: 'Bereavement',
      OTHER: 'Other'
    };
    return labels[type] || type;
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="workforce-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property to manage workforce</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="workforce-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workforce-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Workforce Management</h1>
          <p className="text-muted-foreground">Manage employees, schedules, and time tracking</p>
        </div>
        <Button onClick={loadDashboard} variant="outline" data-testid="refresh-workforce-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-workforce-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="scheduler" data-testid="tab-scheduler">Scheduler</TabsTrigger>
          <TabsTrigger value="timesheet" data-testid="tab-timesheet">Time Tracking</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Leave Management</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-total-employees">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.employees?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.employees?.active || 0} active
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-scheduled-today">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Scheduled Today</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.today?.scheduled || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.today?.checked_in || 0} checked in
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-pending-leave">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Pending Leave</CardTitle>
                <Palmtree className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.pending?.leave_requests || 0}</div>
                <p className="text-xs text-muted-foreground">Awaiting approval</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-pending-time">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Time Approvals</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.pending?.time_approvals || 0}</div>
                <p className="text-xs text-muted-foreground">Pending review</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('employees'); setShowEmployeeDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <UserPlus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Add Employee</h3>
                    <p className="text-sm text-muted-foreground">Register new staff member</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('scheduler'); setShowScheduleDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CalendarDays className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Create Schedule</h3>
                    <p className="text-sm text-muted-foreground">Assign shifts to staff</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('leave'); setShowLeaveDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                    <Palmtree className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Submit Leave</h3>
                    <p className="text-sm text-muted-foreground">Request time off</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Employee Directory</CardTitle>
                  <CardDescription>Manage all staff members</CardDescription>
                </div>
                <Button onClick={() => setShowEmployeeDialog(true)} data-testid="add-employee-btn">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add Employee
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadEmployees()}
                    className="pl-10"
                    data-testid="search-employees"
                  />
                </div>
                <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-department">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={loadEmployees}>
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
              </div>

              {/* Employee Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.id} data-testid={`employee-row-${employee.id}`}>
                      <TableCell>
                        <div className="font-medium">{employee.first_name} {employee.last_name}</div>
                        <div className="text-sm text-muted-foreground">{employee.email}</div>
                      </TableCell>
                      <TableCell>{employee.employee_code}</TableCell>
                      <TableCell>{employee.departments?.name || '-'}</TableCell>
                      <TableCell>{employee.positions?.title || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{employee.employment_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(employee.status)}>
                          {employee.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {employees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No employees found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduler Tab */}
        <TabsContent value="scheduler" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <CardTitle>Week of {format(currentWeekStart, 'MMM d, yyyy')}</CardTitle>
                    <CardDescription>Staff scheduling for the week</CardDescription>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button onClick={() => setShowScheduleDialog(true)} data-testid="add-schedule-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Schedule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[150px]">Employee</TableHead>
                      {getWeekDays().map((day) => (
                        <TableHead key={day.toISOString()} className="min-w-[100px] text-center">
                          <div>{format(day, 'EEE')}</div>
                          <div className="text-xs text-muted-foreground">{format(day, 'MMM d')}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.slice(0, 10).map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell className="font-medium">
                          {employee.first_name} {employee.last_name}
                        </TableCell>
                        {getWeekDays().map((day) => {
                          const daySchedules = schedules.filter(
                            s => s.employee_id === employee.id && 
                            s.schedule_date === format(day, 'yyyy-MM-dd')
                          );
                          return (
                            <TableCell key={day.toISOString()} className="text-center">
                              {daySchedules.length > 0 ? (
                                daySchedules.map((schedule) => (
                                  <div 
                                    key={schedule.id}
                                    className="text-xs p-1 rounded mb-1"
                                    style={{ backgroundColor: schedule.shift_templates?.color || '#3B82F6', color: 'white' }}
                                  >
                                    {schedule.start_time?.slice(0, 5)} - {schedule.end_time?.slice(0, 5)}
                                  </div>
                                ))
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {employees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No employees to schedule
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Tracking Tab */}
        <TabsContent value="timesheet" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <CardTitle>Time Entries</CardTitle>
                    <CardDescription>Week of {format(currentWeekStart, 'MMM d, yyyy')}</CardDescription>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Clock In</TableHead>
                    <TableHead>Clock Out</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Overtime</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeEntries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`time-entry-${entry.id}`}>
                      <TableCell className="font-medium">
                        {entry.employees?.first_name} {entry.employees?.last_name}
                      </TableCell>
                      <TableCell>{entry.entry_date}</TableCell>
                      <TableCell>
                        {entry.clock_in ? format(new Date(entry.clock_in), 'HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        {entry.clock_out ? format(new Date(entry.clock_out), 'HH:mm') : '-'}
                      </TableCell>
                      <TableCell>{entry.total_hours?.toFixed(2) || '-'}</TableCell>
                      <TableCell>
                        {entry.overtime_hours > 0 && (
                          <Badge variant="secondary">{entry.overtime_hours?.toFixed(2)}h OT</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(entry.status)}>
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.status === 'PENDING' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleApproveTimeEntry(entry.id)}
                          >
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {timeEntries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No time entries for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leave Management Tab */}
        <TabsContent value="leave" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Leave Requests</CardTitle>
                  <CardDescription>Manage employee time off requests</CardDescription>
                </div>
                <Button onClick={() => setShowLeaveDialog(true)} data-testid="add-leave-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Request
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaveRequests.map((request) => (
                    <TableRow key={request.id} data-testid={`leave-request-${request.id}`}>
                      <TableCell className="font-medium">
                        {request.employees?.first_name} {request.employees?.last_name}
                      </TableCell>
                      <TableCell>{getLeaveTypeLabel(request.leave_type)}</TableCell>
                      <TableCell>{request.start_date}</TableCell>
                      <TableCell>{request.end_date}</TableCell>
                      <TableCell>{request.days_requested}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{request.reason || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(request.status)}>
                          {request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {request.status === 'PENDING' && (
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleApproveLeave(request.id)}
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRejectLeave(request.id)}
                            >
                              <XCircle className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {leaveRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No leave requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Employee Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
            <DialogDescription>Enter employee details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, first_name: e.target.value})}
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, last_name: e.target.value})}
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Employee Code *</Label>
              <Input
                value={employeeForm.employee_code}
                onChange={(e) => setEmployeeForm({...employeeForm, employee_code: e.target.value})}
                placeholder="EMP001"
                data-testid="input-employee-code"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select 
                value={employeeForm.department_id} 
                onValueChange={(v) => setEmployeeForm({...employeeForm, department_id: v})}
              >
                <SelectTrigger data-testid="select-department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Employment Type</Label>
              <Select 
                value={employeeForm.employment_type} 
                onValueChange={(v) => setEmployeeForm({...employeeForm, employment_type: v})}
              >
                <SelectTrigger data-testid="select-employment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FULL_TIME">Full Time</SelectItem>
                  <SelectItem value="PART_TIME">Part Time</SelectItem>
                  <SelectItem value="CONTRACT">Contract</SelectItem>
                  <SelectItem value="TEMPORARY">Temporary</SelectItem>
                  <SelectItem value="INTERN">Intern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Hire Date *</Label>
              <Input
                type="date"
                value={employeeForm.hire_date}
                onChange={(e) => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                data-testid="input-hire-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateEmployee} data-testid="submit-employee-btn">Create Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Schedule</DialogTitle>
            <DialogDescription>Assign a shift to an employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select 
                value={scheduleForm.employee_id} 
                onValueChange={(v) => setScheduleForm({...scheduleForm, employee_id: v})}
              >
                <SelectTrigger data-testid="select-schedule-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={scheduleForm.schedule_date}
                onChange={(e) => setScheduleForm({...scheduleForm, schedule_date: e.target.value})}
                data-testid="input-schedule-date"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={scheduleForm.start_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                  data-testid="input-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={scheduleForm.end_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                  data-testid="input-end-time"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSchedule} data-testid="submit-schedule-btn">Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Leave Request Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit Leave Request</DialogTitle>
            <DialogDescription>Request time off for an employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select 
                value={leaveForm.employee_id} 
                onValueChange={(v) => setLeaveForm({...leaveForm, employee_id: v})}
              >
                <SelectTrigger data-testid="select-leave-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select 
                value={leaveForm.leave_type} 
                onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}
              >
                <SelectTrigger data-testid="select-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANNUAL">Annual Leave</SelectItem>
                  <SelectItem value="SICK">Sick Leave</SelectItem>
                  <SelectItem value="PERSONAL">Personal</SelectItem>
                  <SelectItem value="MATERNITY">Maternity</SelectItem>
                  <SelectItem value="PATERNITY">Paternity</SelectItem>
                  <SelectItem value="UNPAID">Unpaid Leave</SelectItem>
                  <SelectItem value="BEREAVEMENT">Bereavement</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})}
                  data-testid="input-leave-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})}
                  data-testid="input-leave-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                placeholder="Optional reason"
                data-testid="input-leave-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateLeaveRequest} data-testid="submit-leave-btn">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
